<template>
  <div
    v-if="(isAwaitingDeposit || isBlocked) && !isImpersonating"
    class="mlb-banner"
    :class="{
      'mlb-banner--amber': isAwaitingDeposit,
      'mlb-banner--red': isBlocked,
    }"
    role="alert"
    @click="handleTap"
  >
    <f7-icon
      :f7="isAwaitingDeposit ? 'exclamationmark_circle_fill' : 'xmark_octagon_fill'"
      size="14px"
      class="mlb-icon"
    ></f7-icon>
    <span class="mlb-text">
      <template v-if="isAwaitingDeposit">
        {{
          $t('appBanner.membership.awaiting', {
            n: daysRemaining ?? '—',
          })
        }}
      </template>
      <template v-else>
        {{ $t('appBanner.membership.blocked') }}
      </template>
    </span>
    <f7-icon f7="chevron_right" size="12px" class="mlb-chevron"></f7-icon>
  </div>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed } from 'vue';
import { useMembershipStatus } from '@/composables/useMembershipStatus';
import { useAuthStore } from '@/stores/authStore.js';
import { contactSellersClubAdmin } from '@/utils/supportContact.js';

const { stateKey, daysRemaining, minimumDepositCents } = useMembershipStatus();

// Suppress this seller-shell strip while an operator is impersonating a member:
// ActingAsBanner takes the same fixed top slot, so the two must never stack.
const authStore = useAuthStore();
const isImpersonating = computed(() => authStore.isImpersonating);

const isAwaitingDeposit = computed(() => stateKey.value === 'APPROVED_AWAITING_DEPOSIT');
const isBlocked = computed(() => stateKey.value === 'BLOCKED');

// Parked, not deleted: the activation amount is hidden from the copy by a TEMPORARY
// owner decision (2026-07-16), so keeping the formatter makes restoring the number a
// one-line template change. `-u-nu-latn` forces Western digits (owner-locked).
// eslint-disable-next-line no-unused-vars
const minimumDepositFormatted = computed(() => {
  if (minimumDepositCents.value == null) return '—';
  return (minimumDepositCents.value / 100).toLocaleString('ar-EG-u-nu-latn', {
    style: 'currency',
    currency: 'EGP',
    maximumFractionDigits: 0,
  });
});


function handleTap() {
  if (isAwaitingDeposit.value) {
    // The banner reads «كلّم مسؤول النادي» — it must open the chat, not the deposit
    // form. The self-serve deposit is still reachable from the wallet and the
    // profile/orders cards; this strip is the contact-first path.
    contactSellersClubAdmin();
  } else if (isBlocked.value) {
    f7.views.main.router.navigate('/profile/');
  }
}
</script>

<style scoped>
.mlb-banner {
  position: fixed;
  top: env(safe-area-inset-top, 0px);
  left: 0;
  right: 0;
  z-index: 600; /* above F7 navbar at z-index 500 */
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 32px;
  min-height: 32px;
  cursor: pointer;
  overflow: hidden;
}

.mlb-banner--amber {
  background-color: #d97706; /* amber-600 */
  color: #fff;
}

.mlb-banner--red {
  background-color: #dc2626; /* red-600 */
  color: #fff;
}

.mlb-icon {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.9);
}

.mlb-text {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #fff;
}

.mlb-chevron {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
}
</style>
